import {
  ATTENTION_STATE_LABELS,
  type AttentionState,
  MANAGER_ROLES,
  type AttentionCard,
} from '@performance-crm/shared';
import {
  ArrowDownLeft,
  ArrowUpRight,
  Bot,
  MoreHorizontal,
  PauseCircle,
  RefreshCw,
} from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';
import { ScoreBadge, TemperatureBadge } from '@/components/leads/badges';
import { DesistDialog } from '@/components/leads/dialogs';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Select } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/components/ui/toast-context';
import { useAuth } from '@/lib/auth-context';
import { fmt } from '@/lib/format';
import { useAdvisors, useAttentionAction, useAttentionBoard, useProjects } from '@/lib/queries';
import { useCompanyScope } from '@/lib/useCompanyScope';

const ORDER: AttentionState[] = [
  'POR_CONTACTAR',
  'EN_CONVERSACION',
  'PRIORIDAD',
  'SIN_RESPUESTA',
  'DESCARTE_PROPUESTO',
];
const HINT: Record<AttentionState, string> = {
  POR_CONTACTAR: 'El bot escribió (o va a escribir) y aún no hay respuesta',
  EN_CONVERSACION: 'El cliente responde; el bot conversa',
  PRIORIDAD: 'Caliente o con cita propuesta: le toca al asesor',
  SIN_RESPUESTA: 'Se agotaron los intentos del bot',
  DESCARTE_PROPUESTO: 'El bot propone desistir; lo confirma un humano',
};
const COLUMN_TONE: Record<AttentionState, string> = {
  POR_CONTACTAR: 'border-t-slate-400',
  EN_CONVERSACION: 'border-t-sky-500',
  PRIORIDAD: 'border-t-amber-500',
  SIN_RESPUESTA: 'border-t-red-500',
  DESCARTE_PROPUESTO: 'border-t-purple-500',
};

type ActionKind = 'take' | 'release' | 'stop';

/** Tablero "Atención del bot": leads sin cita por estado de la conversación, con las acciones de intervención (Fase 8). */
export function AttentionPage() {
  const { user } = useAuth();
  const { companies, active } = useCompanyScope();
  const [params, setParams] = useSearchParams();
  const companyId = params.get('companyId') ?? active ?? companies[0]?.companyId;
  const projectId = params.get('projectId') ?? '';
  const advisorId = params.get('advisorId') ?? '';
  const isManager = !!user?.memberships.some(
    (m) => m.companyId === companyId && MANAGER_ROLES.includes(m.role),
  );
  const { data: projects } = useProjects();
  const { data: advisors } = useAdvisors(isManager ? companyId : undefined);
  const { data, isLoading, isFetching, refetch } = useAttentionBoard({
    companyId,
    projectId,
    advisorId,
  });
  const action = useAttentionAction();
  const toast = useToast();
  const [desist, setDesist] = useState<AttentionCard | null>(null);
  const set = (k: string, v: string) => {
    const next = new URLSearchParams(params);
    if (v) next.set(k, v);
    else next.delete(k);
    if (k === 'companyId') {
      next.delete('projectId');
      next.delete('advisorId');
    }
    setParams(next, { replace: true });
  };
  const run = (card: AttentionCard, kind: ActionKind) => {
    const done = {
      take: 'Conversación tomada: el bot queda en pausa',
      release: 'Conversación devuelta al bot',
      stop: 'Lead retirado del bot',
    }[kind];
    action
      .mutateAsync({ leadId: card.leadId, action: kind })
      .then(() => toast.success(done))
      .catch((e: Error) => toast.error('No se pudo aplicar', e.message));
  };
  const companyProjects = (projects ?? []).filter((p) => p.companyId === companyId);
  const humanAdvisors = (advisors ?? []).filter((a) => a.kind === 'HUMAN');
  const col = (s: AttentionState) => data?.columns.find((c) => c.state === s);
  const tile = (label: string, value: number | undefined, tone = '') => (
    <Card>
      <CardContent className="p-3">
        <p className="text-muted-foreground text-[11px] uppercase">{label}</p>
        <p className={`text-2xl font-semibold ${tone}`}>{value ?? '—'}</p>
      </CardContent>
    </Card>
  );

  return (
    <div className="flex h-full min-h-0 flex-col gap-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Atención del bot</h1>
          <p className="text-muted-foreground text-sm">
            Leads sin cita que atiende el bot. El asesor responsable interviene cuando el lead está
            caliente; si no registra nada en el plazo, el bot retoma.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {companies.length > 1 && (
            <Select
              value={companyId ?? ''}
              onChange={(e) => set('companyId', e.target.value)}
              aria-label="Empresa"
              className="w-52"
            >
              {companies.map((c) => (
                <option key={c.companyId} value={c.companyId}>
                  {c.companyName}
                </option>
              ))}
            </Select>
          )}
          <Select
            value={projectId}
            onChange={(e) => set('projectId', e.target.value)}
            aria-label="Proyecto"
            className="w-48"
          >
            <option value="">Todos los proyectos</option>
            {companyProjects.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </Select>
          {isManager && (
            <Select
              value={advisorId}
              onChange={(e) => set('advisorId', e.target.value)}
              aria-label="Asesor"
              className="w-48"
            >
              <option value="">Todos los asesores</option>
              {humanAdvisors.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.fullName}
                </option>
              ))}
            </Select>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={() => void refetch()}
            disabled={isFetching}
            title="Actualizar"
          >
            <RefreshCw className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
        {tile('En el tablero', data?.total)}
        {tile('Por contactar', col('POR_CONTACTAR')?.count)}
        {tile('Prioridad', col('PRIORIDAD')?.count, 'text-warning')}
        {tile('Sin respuesta', col('SIN_RESPUESTA')?.count, 'text-danger')}
        {tile('En pausa por un asesor', data?.pausedCount)}
      </div>

      {isLoading && <Skeleton className="h-64 w-full" />}
      {data && !data.bot.ready && (
        <p
          className="border-warning/60 bg-warning/10 flex items-start gap-2 rounded-md border p-3 text-sm"
          data-testid="attention-bot-off"
        >
          <AlertTriangle className="text-warning mt-0.5 h-4 w-4 shrink-0" />
          <span>
            <span className="font-medium">{data.bot.reason ?? 'El bot no puede atender'}</span>.
            Mientras tanto no escribe, no responde ni retoma: los leads de este tablero siguen con
            su asesor y los nuevos reciben la tarea «Primer contacto».
            {isManager && (
              <>
                {' '}
                <Link to={`/admin?tab=bot&companyId=${companyId ?? ''}`} className="underline">
                  Administración → Bot de atención
                </Link>
              </>
            )}
          </span>
        </p>
      )}
      {data && data.total === 0 && (
        <p
          className="text-muted-foreground rounded-md border border-dashed p-4 text-sm"
          data-testid="attention-empty"
        >
          Ningún lead en el tablero. Para que los leads nuevos de un proyecto entren aquí, marca "El
          bot de atención escribe primero a los leads nuevos" en{' '}
          <Link to={`/equipo?tab=reglas&companyId=${companyId ?? ''}`} className="underline">
            Equipo → Distribución
          </Link>
          .
        </p>
      )}
      {data && data.total > 0 && (
        <div
          className="flex min-h-0 flex-1 gap-3 overflow-x-auto pb-2"
          data-testid="attention-board"
        >
          {ORDER.map((state) => {
            const c = col(state);
            return (
              <section
                key={state}
                className={`bg-muted/40 flex w-72 shrink-0 flex-col rounded-lg border border-t-4 ${COLUMN_TONE[state]}`}
                data-testid={`attention-col-${state}`}
              >
                <header className="px-3 py-2">
                  <p className="flex items-center justify-between text-sm font-medium">
                    {ATTENTION_STATE_LABELS[state]}
                    <span className="text-muted-foreground text-xs" data-testid="col-count">
                      {c?.count ?? 0}
                    </span>
                  </p>
                  <p className="text-muted-foreground text-[11px]">
                    {state === 'POR_CONTACTAR' && data && !data.bot.ready
                      ? 'Bot apagado: no escribe; el lead sigue con su asesor'
                      : HINT[state]}
                  </p>
                </header>
                <div className="flex-1 space-y-2 overflow-y-auto px-2 pb-2">
                  {c?.cards.map((card) => (
                    <BoardCard
                      key={card.leadId}
                      card={card}
                      isManager={isManager}
                      onAction={(k) => run(card, k)}
                      onDesist={() => setDesist(card)}
                    />
                  ))}
                  {(c?.cards.length ?? 0) === 0 && (
                    <p className="text-muted-foreground rounded-md border border-dashed p-3 text-center text-xs">
                      Sin leads
                    </p>
                  )}
                </div>
              </section>
            );
          })}
        </div>
      )}
      {desist && companyId && (
        <DesistDialog
          leadId={desist.leadId}
          companyId={companyId}
          open
          onOpenChange={(o) => {
            if (!o) {
              setDesist(null);
              void refetch();
            }
          }}
        />
      )}
    </div>
  );
}

function BoardCard({
  card,
  isManager,
  onAction,
  onDesist,
}: {
  card: AttentionCard;
  isManager: boolean;
  onAction: (kind: ActionKind) => void;
  onDesist: () => void;
}) {
  const navigate = useNavigate();
  const a = card.attention;
  const paused = a.owner === 'HUMAN';
  const last = card.lastMessage;
  return (
    <article
      className="bg-card rounded-md border p-2.5 text-sm shadow-sm"
      data-testid="attention-card"
    >
      <div className="flex items-start gap-1">
        <Link
          to={`/leads/${card.leadId}`}
          className="min-w-0 flex-1 truncate font-medium hover:underline"
        >
          {card.contactName}
        </Link>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="text-muted-foreground hover:bg-accent rounded p-0.5"
              aria-label="Acciones"
              data-testid="card-menu"
            >
              <MoreHorizontal className="h-4 w-4" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="w-60">
            <DropdownMenuItem onSelect={() => navigate(`/leads/${card.leadId}`)}>
              Ver ficha
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            {!paused && (
              <DropdownMenuItem onSelect={() => onAction('take')} data-testid="menu-take">
                <PauseCircle className="h-4 w-4" /> Tomar la conversación (bot en pausa)
              </DropdownMenuItem>
            )}
            {paused && (
              <DropdownMenuItem onSelect={() => onAction('release')} data-testid="menu-release">
                <Bot className="h-4 w-4" /> Devolver al bot
              </DropdownMenuItem>
            )}
            {a.state === 'DESCARTE_PROPUESTO' && (
              <DropdownMenuItem onSelect={onDesist} data-testid="menu-desist">
                Confirmar descarte…
              </DropdownMenuItem>
            )}
            {isManager && (
              <DropdownMenuItem onSelect={() => onAction('stop')} data-testid="menu-stop">
                Retirar del bot
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      <p className="text-muted-foreground mt-0.5 truncate text-xs">
        {card.projectName} · {card.advisorName ?? 'Sin asesor'}
        {card.phone ? ` · ${card.phone}` : ''}
      </p>
      <div className="mt-2 flex flex-wrap items-center gap-1">
        <TemperatureBadge temperature={card.temperature} />
        <ScoreBadge score={card.score} />
        {paused ? (
          <Badge tone="muted" title="Un asesor tomó la conversación; el bot está en pausa">
            <PauseCircle className="h-3 w-3" /> {card.pausedByName ?? 'Asesor'} ·{' '}
            {fmt.ago(a.pausedAt)}
          </Badge>
        ) : (
          <Badge tone="primary" title="El bot está atendiendo">
            <Bot className="h-3 w-3" /> Bot
            {a.attempts ? ` · ${a.attempts} ${a.attempts === 1 ? 'intento' : 'intentos'}` : ''}
          </Badge>
        )}
      </div>
      {last && (
        <p className="text-muted-foreground mt-2 flex items-start gap-1 text-xs">
          <span
            className="mt-0.5 shrink-0"
            title={last.direction === 'INBOUND' ? 'Escribió el cliente' : 'Escribió el bot'}
          >
            {last.direction === 'INBOUND' ? (
              <ArrowDownLeft className="h-3 w-3" />
            ) : (
              <ArrowUpRight className="h-3 w-3" />
            )}
          </span>
          <span className="line-clamp-2 min-w-0 flex-1">{last.text ?? '(adjunto)'}</span>
          <span className="shrink-0">{fmt.ago(last.sentAt)}</span>
        </p>
      )}
      {a.note && <p className="mt-1 line-clamp-2 text-xs italic">{a.note}</p>}
      {a.nextAt && !paused && a.state === 'POR_CONTACTAR' && (
        <p className="text-muted-foreground mt-1 text-[11px]">
          Próximo intento {fmt.dateTime(a.nextAt)}
        </p>
      )}
    </article>
  );
}
